/* eslint-disable @typescript-eslint/no-explicit-any */

import { Animation, createAnimation } from '@ionic/core';
import {
  calculateWindowAdjustment,
  getArrowDimensions,
  getElementRoot,
  getPopoverDimensions,
  getPopoverPosition,
  shouldShowArrow,
} from './popover.utils';

const POPOVER_IOS_BODY_PADDING = 5;

/**
 * iOS Popover Enter Animation
 */
// TODO(FW-2832): types
export const iosPopoverTopEnterAnimation = (
  baseEl: HTMLElement,
  opts?: any
): Animation => {
  const { event: ev, size, trigger, reference, side, align } = opts;
  const doc = baseEl.ownerDocument as any;
  const isRTL = doc.dir === 'rtl';
  const bodyWidth = doc.defaultView.innerWidth;
  const bodyHeight = doc.defaultView.innerHeight;

  const root = getElementRoot(baseEl);
  const contentEl = root.querySelector('.popover-content') as HTMLElement;
  const arrowEl = root.querySelector('.popover-arrow') as HTMLElement | null;

  const referenceSizeEl = trigger || ev?.detail?.ionShadowTarget || ev?.target;
  const { contentWidth, contentHeight } = getPopoverDimensions(
    size,
    contentEl,
    referenceSizeEl
  );
  const { arrowWidth, arrowHeight } = getArrowDimensions(arrowEl);

  const defaultPosition = {
    top: bodyHeight / 2 - contentHeight / 2,
    left: bodyWidth / 2 - contentWidth / 2,
    originX: isRTL ? 'right' : 'left',
    originY: 'top',
  };

  const results = getPopoverPosition(
    isRTL,
    contentWidth,
    contentHeight,
    arrowWidth,
    arrowHeight,
    reference,
    side,
    align,
    defaultPosition,
    trigger,
    ev
  );

  const padding = size === 'cover' ? 0 : POPOVER_IOS_BODY_PADDING;
  const margin = size === 'cover' ? 0 : 25;

  const {
    originX,
    originY,
    top,
    left,
    bottom,
    checkSafeAreaLeft,
    checkSafeAreaRight,
    arrowTop,
    arrowLeft,
    addPopoverBottomClass,
  } = calculateWindowAdjustment(
    side,
    results.top,
    results.left,
    padding,
    bodyWidth,
    bodyHeight,
    contentWidth,
    contentHeight,
    margin,
    results.originX,
    results.originY,
    results.referenceCoordinates,
    results.arrowTop,
    results.arrowLeft,
    arrowHeight
  );

  let adjustedBottom = bottom;

  if (contentHeight === 0) {
    const referenceRect = referenceSizeEl?.getBoundingClientRect();
    const targetTop = referenceRect?.top;

    if (targetTop !== undefined && bodyHeight - targetTop < targetTop) {
      adjustedBottom = bodyHeight - targetTop + arrowHeight;
    }
  }

  const baseAnimation = createAnimation();
  const backdropAnimation = createAnimation();
  const contentAnimation = createAnimation();

  backdropAnimation
    .addElement(root.querySelector('ion-backdrop')!)
    .fromTo('opacity', 0.01, 'var(--backdrop-opacity)')
    .beforeStyles({
      'pointer-events': 'none',
    })
    .afterClearStyles(['pointer-events']);

  // In Chromium, if the wrapper animates, the backdrop filter doesn't work.
  // The Chromium team stated that this behavior is expected and not a bug. The element animating opacity creates a backdrop root for the backdrop-filter.
  // To get around this, instead of animating the wrapper, animate both the arrow and content.
  // https://bugs.chromium.org/p/chromium/issues/detail?id=1148826
  contentAnimation
    .addElement(root.querySelector('.popover-arrow')!)
    .addElement(root.querySelector('.popover-content')!)
    .fromTo('opacity', 0.01, 1);
  // TODO(FW-4376) Ensure that arrow also blurs when translucent

  return baseAnimation
    .easing('ease')
    .duration(100)
    .beforeAddWrite(() => {
      if (size === 'cover') {
        baseEl.style.setProperty('--width', `${contentWidth}px`);
      }

      if (addPopoverBottomClass) {
        baseEl.classList.add('popover-bottom');
      }

      if (adjustedBottom !== undefined) {
        contentEl.style.setProperty('bottom', `${adjustedBottom}px`);
      }

      const safeAreaLeft = ' + var(--ion-safe-area-left, 0)';
      const safeAreaRight = ' - var(--ion-safe-area-right, 0)';

      let leftValue = `${left}px`;

      if (checkSafeAreaLeft) {
        leftValue = `${left}px${safeAreaLeft}`;
      }
      if (checkSafeAreaRight) {
        leftValue = `${left}px${safeAreaRight}`;
      }

      if (adjustedBottom === undefined) {
        contentEl.style.setProperty(
          'top',
          `calc(${top}px + var(--offset-y, 0))`
        );
      }

      contentEl.style.setProperty(
        'left',
        `calc(${leftValue} + var(--offset-x, 0))`
      );
      contentEl.style.setProperty('transform-origin', `${originY} ${originX}`);

      if (arrowEl !== null) {
        const didAdjustBounds = results.top !== top || results.left !== left;
        const showArrow = shouldShowArrow(side, didAdjustBounds, ev, trigger);

        if (showArrow) {
          const adjustedArrowTop =
            adjustedBottom !== undefined
              ? bodyHeight - adjustedBottom
              : arrowTop;

          arrowEl.style.setProperty(
            'top',
            `calc(${adjustedArrowTop}px + var(--offset-y, 0))`
          );
          arrowEl.style.setProperty(
            'left',
            `calc(${arrowLeft}px + var(--offset-x, 0))`
          );

          if (adjustedBottom !== undefined) {
            arrowEl.style.setProperty('transform', `rotate(180deg)`);
          } else {
            arrowEl.style.setProperty('transform', `rotate(0deg)`);
          }
        } else {
          arrowEl.style.setProperty('display', 'none');
        }
      }
    })
    .addAnimation([backdropAnimation, contentAnimation]);
};
